import * as React from 'react'
import { useNavigate, useLocation } from 'react-router-dom'

export type TocChild = { id: string; title: string }
export type TocItem = { id: string; title: string; children?: TocChild[] }

type Options = {
  topOffset?: number
  updateHashOnClick?: boolean
}

function sanitizeToc(toc: TocItem[] = []): Required<TocItem>[] {
  return (toc ?? [])
    .filter((s): s is TocItem => Boolean(s?.id?.trim()) && Boolean(s?.title?.trim()))
    .map((s) => ({
      ...s,
      children: (s.children ?? []).filter(
        (c) => Boolean(c?.id?.trim()) && Boolean(c?.title?.trim()),
      ),
    }))
}

function getHeadings(toc: Required<TocItem>[]) {
  return toc
    .flatMap((s) => [s.id, ...s.children.map((c) => c.id)])
    .map((id) => {
      const el = document.getElementById(id)
      if (!el) return null
      const style = window.getComputedStyle(el)
      const scrollMt = parseFloat(style.scrollMarginTop || '0')
      const top = window.scrollY + el.getBoundingClientRect().top - scrollMt
      return { id, top }
    })
    .filter((x): x is { id: string; top: number } => !!x)
    .sort((a, b) => a.top - b.top)
}

export function useTocScrollSpy(tableOfContents: TocItem[] = [], opts: Options = {}) {
  const { topOffset = 100, updateHashOnClick = true } = opts
  const navigate = useNavigate()
  const { pathname, search } = useLocation()

  const toc = React.useMemo(() => sanitizeToc(tableOfContents), [tableOfContents])

  const [activeId, setActiveId] = React.useState<string | null>(toc[0]?.id ?? null)
  // While true, scroll-driven updates to activeId are suppressed.
  const suppressSpyRef = React.useRef(false)
  const suppressTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  React.useEffect(() => {
    if (!toc.length) return
    if (typeof window === 'undefined') return

    let headings = getHeadings(toc)
    let raf = 0

    const scrollRecalculation = () => {
      headings = getHeadings(toc)
      onScroll()
    }

    const onScroll = () => {
      if (suppressSpyRef.current) return

      if (raf) cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => {
        if (suppressSpyRef.current) return
        const y = window.scrollY + topOffset
        let current = headings[0]?.id ?? null
        for (const h of headings) {
          if (y >= h.top) current = h.id
          else break
        }
        setActiveId(current)
      })
    }

    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', scrollRecalculation)

    const tm = setTimeout(scrollRecalculation, 200)

    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', scrollRecalculation)
      clearTimeout(tm)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [toc, topOffset])

  const isActive = React.useCallback(
    (item: { id: string }) => {
      if (!activeId) return false
      return item.id === activeId
    },
    [activeId],
  )

  const onTocClick = React.useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, targetId: string) => {
      e.preventDefault()
      const el = document.getElementById(targetId)
      if (!el) return

      // Lock activeId to the clicked item and ignore scroll-spy
      // updates until the smooth-scroll animation has settled.
      suppressSpyRef.current = true
      setActiveId(targetId)

      el.scrollIntoView({ behavior: 'smooth', block: 'start' })

      if (updateHashOnClick) {
        navigate({ pathname, search, hash: `#${targetId}` })
      }

      if (suppressTimeoutRef.current) clearTimeout(suppressTimeoutRef.current)
      suppressTimeoutRef.current = setTimeout(() => {
        suppressSpyRef.current = false
      }, 600)
    },
    [navigate, pathname, search, updateHashOnClick],
  )

  React.useEffect(() => {
    return () => {
      if (suppressTimeoutRef.current) clearTimeout(suppressTimeoutRef.current)
    }
  }, [])

  return { toc, activeId, isActive, onTocClick }
}
